import { determinePhaseClassification } from '@/components/drill-library/lib/phase-classification';
import type { AnalysisData } from '@/components/posture-analysis/types';
import type { FormValues, Analysis } from '@/lib/types';
import type { OverallClassification } from '@/lib/posture-analysis/types';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';

/**
 * Full result of a client classification compute: the short code that gets
 * persisted on `client.classification` PLUS the complete
 * {@link OverallClassification} the server computed it from (pattern, phase,
 * movementLimitations, …). The overall object powers needs-tag auto-derivation
 * (`deriveAndMergeClientNeeds`): Biomechanical Intent / Movement Limitation
 * needs can only be derived from `overall.movementLimitations`, never from the
 * short string alone.
 */
export interface ClientClassificationDetail {
  shortCode: string;
  overall: OverallClassification;
}

function mapPatternToCode(pattern: string | null | undefined): string {
  if (!pattern) return 'N';
  const p = pattern.toLowerCase();
  if (p.includes('swayback')) return 'SB';
  if (p.includes('anterior')) return 'APT';
  if (p.includes('neutral')) return 'N';
  return pattern.slice(0, 3).toUpperCase();
}

function mapPhaseToCode(phase: string): string {
  if (phase.startsWith('P1')) return 'P1';
  if (phase.startsWith('P2')) return 'P2';
  if (phase.startsWith('P3')) return 'P3';
  return 'NA';
}

/**
 * Returns the full classification detail, or `null` when it could not be
 * computed (no analysis data / classify API unavailable). `null` means
 * "don't know" — callers must skip persisting (and skip needs derivation)
 * in that case. See {@link computeShortClientClassification} for why `null`
 * must never be conflated with a real `'N'` (Neutral) result.
 */
export async function computeClientClassificationDetail(
  analysisData: AnalysisData | null | undefined
): Promise<ClientClassificationDetail | null> {
  if (!analysisData) return null;
  const { apiFetch } = await import('@/lib/api-client');

  const postureResult = await apiFetch<any>('/posture-analyses/classify', {
    method: 'POST',
    body: JSON.stringify({ analysisData })
  }).catch(() => null);

  if (!postureResult) return null;
  const phase = await determinePhaseClassification(analysisData);

  // Create override mapping
  const overrideMap: Record<string, 'P1' | 'P2' | 'P3'> = {
    'P1-APT': 'P1',
    'P1-SB': 'P1',
    'P2': 'P2',
    'P3': 'P3'
  };
  const phaseOverride = overrideMap[phase] as 'P1' | 'P2' | 'P3';

  const overall = await apiFetch<OverallClassification>('/posture-analyses/overall-classification', {
    method: 'POST',
    body: JSON.stringify({ postureResult, phaseOverride })
  });

  return { shortCode: overall.shortCode, overall };
}

/**
 * Returns the short classification code, or `null` when it could not be computed
 * (no analysis data / classify API unavailable). `null` means "don't know" —
 * callers must skip persisting in that case. A literal `'N'` is a real computed
 * Neutral result; conflating the two lets a transient API failure overwrite a
 * client's stored classification with Neutral.
 */
export async function computeShortClientClassification(
  analysisData: AnalysisData | null | undefined
): Promise<string | null> {
  const detail = await computeClientClassificationDetail(analysisData);
  return detail?.shortCode ?? null;
}

function parseNumber(v: any): number | null {
  if (v === null || v === undefined || v === '' || v === 'Select...') return null;
  const m = String(v).match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}

/**
 * SOP variant of {@link computeClientClassificationDetail}. The returned
 * `shortCode` preserves the historical pattern-fallback behavior (an `'N'`
 * pattern falls back to the first token of `previousClassification`); the
 * `overall` object is the server's computation, untouched.
 */
export async function computeClientClassificationDetailFromSop(
  formValues: FormValues,
  analysis: Analysis | null,
  previousClassification?: string,
  ruleDocVersion: RuleDocVersion = 'current'
): Promise<ClientClassificationDetail> {
  const { apiFetch } = await import('@/lib/api-client');

  // Use new SOP API to generate the SopPostureResult
  const sopResult = await apiFetch<any>('/assessments/evaluate-sop-posture', {
    method: 'POST',
    body: JSON.stringify({ formValues, analysis })
  });

  // Use new API 3 to assemble the classification string using SOP priority
  const overall = await apiFetch<OverallClassification>('/posture-analyses/overall-classification', {
     method: 'POST',
     body: JSON.stringify({ sopResult })
  });

  // Map back to original format string if pattern missing
  let patternCode: string = overall.pattern;
  if (patternCode === 'N' && previousClassification) {
    const first = previousClassification.split(',')[0]?.trim();
    if (first) patternCode = first;
  }

  const parts = [patternCode];
  if (overall.lateralized) parts.push(overall.lateralized === 'Left' ? 'L Lat' : 'R Lat');
  if (overall.grounded) parts.push(overall.grounded === 'Left' ? 'L Grd' : 'R Grd');
  parts.push(overall.phase);
  return { shortCode: parts.join(', '), overall };
}

export async function computeShortClientClassificationFromSop(
  formValues: FormValues,
  analysis: Analysis | null,
  previousClassification?: string,
  ruleDocVersion: RuleDocVersion = 'current'
): Promise<string> {
  const detail = await computeClientClassificationDetailFromSop(
    formValues,
    analysis,
    previousClassification,
    ruleDocVersion
  );
  return detail.shortCode;
}
